import React, { FC, useEffect, useRef } from 'react';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { useWindowDimensions } from 'src/hooks';
import {
  Body,
  ButtonContainer,
  CancelButton,
  ConfirmButton,
  StyledDialog,
  Title,
} from './ConfirmDialog.styles';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  body?: string;
  onCancel: () => void;
  cancelButton?: React.ReactNode;
  onConfirm: () => void;
  confirmButton?: React.ReactNode;
  isConfirmButtonDisabled?: boolean;
  loading?: boolean;
  'data-testid'?: string;
}

export const ConfirmDialog: FC<Props> = ({
  open,
  setOpen,
  title,
  body,
  onCancel,
  cancelButton,
  onConfirm,
  confirmButton,
  isConfirmButtonDisabled,
  loading,
  ...props
}) => {
  const { width } = useWindowDimensions();
  const ref = useRef<HTMLDialogElement>(null);
  const closeDialog = () => {
    setOpen(false);
  };

  useEffect(() => {
    ref.current?.addEventListener('close', () => {
      closeDialog();
    });
    return ref.current?.removeEventListener('close', () => {
      closeDialog();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = 'hidden';
      ref.current?.showModal?.();
    } else {
      document.body.style.overflowY = 'auto';
      ref.current?.close?.();
    }
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [open]);

  return (
    <StyledDialog
      show={open}
      data-testid={`${props['data-testid']}-container`}
      ref={ref}
      screenWidth={width}
    >
      {title && (
        <Title data-testid={`${props['data-testid']}-title`}>{title}</Title>
      )}
      {body && <Body data-testid={`${props['data-testid']}-body`}>{body}</Body>}
      {loading && isConfirmButtonDisabled ? (
        <ButtonContainer>
          <SpinnerButton
            disabled={isConfirmButtonDisabled}
            loading={loading}
            variant="primary"
          />
        </ButtonContainer>
      ) : (
        <ButtonContainer>
          {cancelButton ?? (
            <CancelButton
              onClick={onCancel}
              data-testid={`${props['data-testid']}-cancel`}
            >
              <img
                src="/assets/manualIntake/cancel.svg"
                width={'13px'}
                height={'13px'}
                alt="Cancel"
              />
              CANCEL
            </CancelButton>
          )}
          {confirmButton ?? (
            <ConfirmButton
              onClick={onConfirm}
              data-testid={`${props['data-testid']}-confirm`}
              disabled={isConfirmButtonDisabled}
            >
              <img
                src="/assets/manualIntake/submitReport.svg"
                width={'13px'}
                height={'13px'}
                alt="Confirm"
              />
              CONFIRM
            </ConfirmButton>
          )}
        </ButtonContainer>
      )}
    </StyledDialog>
  );
};
